import type { PromoImage } from '@levamar/shared';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:3200';

/**
 * Surfaced API error. Carries `code` (from the api's error JSON) so callers can switch on
 * specific failure modes (eg `licence_not_valid`) instead of regex-matching the raw text.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly payload: Record<string, unknown>;
  constructor(status: number, payload: Record<string, unknown>) {
    super(
      typeof payload.message === 'string' ? payload.message : ((payload.error as string) ?? `api ${status}`),
    );
    this.status = status;
    this.code = (payload.error as string) ?? 'unknown';
    this.payload = payload;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/v1${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(res.status, payload);
  }
  return (await res.json()) as T;
}

function callJson<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
  return call<T>(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export interface DashboardSummary {
  activeBookings: number;
  upcomingBookings: number;
  fleetActive: number;
  fleetTotal: number;
  /** Vehicles with status = maintenance. */
  fleetMaintenance: number;
  customersTotal: number;
  reviewsPending: number;
  /** Payments in state = failed (Vinti4 declines etc.). */
  paymentsFailed: number;
  grossLast30d: { eur: number; cve: number };
  depositsCollectedLast30d: number;
}

export interface BookingRow {
  id: string;
  code: string;
  state: string;
  fromAt: string;
  toAt: string;
  vehicle: { plate: string; name: string };
  customer: { email: string; name: string | null };
}

export interface BookingDetail {
  id: string;
  code: string;
  state: string;
  channel: string;
  fromAt: string;
  toAt: string;
  adminNotes: Record<string, string> | null;
  depositPct: number;
  balance: { eur: number; cve: number } | null;
  vehicle: { id: string; plate: string; name: string };
  customer: { id: string; email: string; name: string | null; phone: string | null };
  pickup: { id: string; name: string } | null;
  returnLoc: { id: string; name: string } | null;
}

export interface VehicleRow {
  id: string;
  plate: string;
  status: 'draft' | 'active' | 'maintenance' | 'retired';
  year: number;
  color: string | null;
  km: number;
  modelId: string;
  name: string;
  bodyType: 'suv' | 'sedan' | 'pickup';
  defaultColor: string | null;
  eurPerDay: number;
  cvePerDay: number;
  eurCaucao: number;
  cveCaucao: number;
  weeklyDiscountPct: number;
  photoCount: number;
  coverKey: string | null;
  coverWidth: number;
}

export type Locale = 'pt' | 'en' | 'fr' | 'es' | 'de';
export type Tr = Record<Locale, string>;

export interface PhotoDescriptor {
  key: string;
  blurhash: string;
  blurDataUrl: string;
  width: number;
  height: number;
  position: number;
  alt: Tr;
}

export interface ModelRow {
  id: string;
  name: Tr;
  nameLocale: string;
  slug: Tr;
  category: string;
  bodyType: 'suv' | 'sedan' | 'pickup';
  seats: number;
  transmission: 'AT' | 'MT';
  fuel: 'gasoline' | 'diesel' | 'hybrid';
  defaultColor: string | null;
  doors: number | null;
  engineCc: number | null;
  engineHp: number | null;
  consumptionL100km: string | null;
  luggageLarge: number | null;
  luggageSmall: number | null;
  acType: string | null;
  multimedia: string | null;
  tripStyles: string[];
  drivetrain: string | null;
  unlimitedKm: boolean | null;
  bookingEnabled: boolean;
}

export interface VehicleDetail {
  id: string;
  plate: string;
  status: VehicleRow['status'];
  year: number;
  vin: string | null;
  color: string | null;
  km: number;
  showOnHome: boolean;
  model: {
    id: string;
    name: Tr;
    nameLocale: string;
    bodyType: 'suv' | 'sedan' | 'pickup';
    defaultColor: string | null;
    seats: number;
    transmission: 'AT' | 'MT';
    fuel: 'gasoline' | 'diesel' | 'hybrid';
  };
  pricing: {
    eurPerDay: number;
    cvePerDay: number;
    eurCaucao: number;
    cveCaucao: number;
    weeklyDiscountPct: number;
  };
  photos: PhotoDescriptor[];
  bookings: {
    recent: { id: string; code: string; state: string; fromAt: string; toAt: string }[];
    upcoming: { id: string; code: string; state: string; fromAt: string; toAt: string }[];
  };
  deletedAt: string | null;
}

export interface AvailabilityResponse {
  from: string;
  to: string;
  bookings: { id: string; code: string; state: string; fromAt: string; toAt: string }[];
  blocks: { id: string; fromAt: string; toAt: string; reason: string }[];
}

export interface LocationRow {
  id: string;
  name: Tr;
  nameLocale: string;
  address: Tr;
  addressLocale: string;
  lat: string | null;
  lng: string | null;
  eurFee: number;
  cveFee: number;
  active: boolean;
  createdAt: string;
}

export interface LocationDetail {
  id: string;
  name: Tr;
  address: Tr;
  lat: string | null;
  lng: string | null;
  eurFee: number;
  cveFee: number;
  active: boolean;
  deletedAt: string | null;
}

export interface LocationFormBody {
  name: Tr;
  address: Tr;
  lat?: number | null;
  lng?: number | null;
  eurFee: number;
  cveFee: number;
  active: boolean;
}

export interface AddonRow {
  id: string;
  key: string;
  name: Tr;
  description: Tr;
  eurDefault: number;
  cveDefault: number;
  active: boolean;
}

export interface AuditRow {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  targetTable: string;
  targetId: string;
  before: unknown;
  after: unknown;
  ip: string | null;
  at: string;
}

export interface CustomerDetail {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  country: string | null;
  localePref: string;
  currencyPref: string;
  marketingOptIn: boolean;
  pointsBalance: number;
  blockedAt: string | null;
  blockedReason: string | null;
  createdAt: string;
  bookings: { id: string; code: string; state: string; fromAt: string; toAt: string }[];
  payments: {
    id: string;
    bookingId: string;
    kind: string;
    method: string;
    amountEur: number;
    amountCve: number;
    state: string;
    createdAt: string;
  }[];
  reviews: { id: string; stars: number; state: string; createdAt: string }[];
}

export interface AddonFormBody {
  key: string;
  name: Tr;
  description: Tr;
  eurDefault: number;
  cveDefault: number;
  active: boolean;
}

export interface CustomerRow {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  country: string | null;
  createdAt: string;
  bookingsCount: number;
}

export interface ReviewRow {
  id: string;
  bookingCode: string;
  authorName: string;
  stars: number;
  body: Record<string, string>;
  preview: string;
  sourceLocale: string;
  vehicleName: string;
  state: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface PaymentRow {
  id: string;
  bookingId: string;
  bookingCode: string;
  customerEmail: string;
  kind: 'deposit' | 'balance' | 'refund' | 'caucao';
  method: string;
  amountEur: number;
  amountCve: number;
  state: 'pending' | 'succeeded' | 'failed' | 'refunded';
  /** Pre-signed S3 URL for the customer's transfer receipt (transfer payments only). 10-minute TTL. */
  proofUrl: string | null;
  proofUploadedAt: string | null;
  createdAt: string;
}

export interface LicenceRow {
  id: string;
  bookingId: string | null;
  status: 'pending' | 'valid' | 'rejected' | 'expired';
  rejectionReason: string | null;
  reviewedAt: string | null;
  expiry: string | null;
  mimeType: string;
  bytes: number;
  createdAt: string;
  /** 5-minute pre-signed image URL from S3/MinIO so the admin can preview the upload. */
  signedUrl: string;
}

export interface RevenueTrendItem {
  day: string;
  eur: number;
}

export const adminApi = {
  me: () => call<{ id: string; email: string; role: string; name: string | null }>('/me'),
  dashboard: () => call<DashboardSummary>('/admin/dashboard'),
  revenueTrend: (days = 14) =>
    call<{ items: RevenueTrendItem[] }>(`/admin/dashboard/revenue-trend?days=${days}`),

  bookings: (params: { q?: string; state?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]);
    const path = qs.toString() ? `/admin/bookings?${qs}` : '/admin/bookings';
    return call<{ items: BookingRow[]; paging: { limit: number; offset: number; count: number } }>(path);
  },
  bookingDetail: (id: string) => call<BookingDetail>(`/admin/bookings/${id}`),
  bookingPayments: (id: string) =>
    call<{ items: PaymentRow[] }>(`/admin/payments?bookingId=${id}`).catch(() => ({ items: [] })),
  cancelBooking: (id: string, body: { reason: string; refund: boolean }) =>
    callJson<{ ok: true; refund: boolean }>(`/admin/bookings/${id}/cancel`, 'POST', body),
  confirmDeposit: (id: string, body: { transactionId?: string; note?: string }) =>
    callJson<{ ok: true; paymentId: string }>(`/admin/bookings/${id}/confirm-deposit`, 'POST', body),
  customerLicences: (customerId: string) =>
    call<{ items: LicenceRow[] }>(`/admin/customers/${customerId}/licences`),
  approveLicence: (licenceId: string, body: { expiry?: string } = {}) =>
    callJson<{ ok: true }>(`/admin/licences/${licenceId}/approve`, 'POST', body),
  rejectLicence: (licenceId: string, body: { reason: string }) =>
    callJson<{ ok: true }>(`/admin/licences/${licenceId}/reject`, 'POST', body),
  chargeBalance: (
    id: string,
    body: {
      method: 'vinti4' | 'transfer' | 'cash' | 'multibanco';
      amountEur: number;
      amountCve: number;
      transactionId?: string;
    },
  ) => callJson<{ paymentId: string }>(`/admin/bookings/${id}/charge-balance`, 'POST', body),
  pickupBooking: (id: string, body: { confirmPlate: string; notes?: string }) =>
    callJson<{ ok: true; state: string }>(`/admin/bookings/${id}/pickup`, 'POST', body),
  returnBooking: (
    id: string,
    body: {
      kmReturned: number;
      fuelLevel: 'empty' | 'quarter' | 'half' | 'three_quarters' | 'full';
      condition: 'ok' | 'minor_damage' | 'major_damage';
      notes?: string;
      caucao?: {
        amountEur: number;
        amountCve: number;
        method: 'vinti4' | 'transfer' | 'cash';
        transactionId?: string;
      };
    },
  ) => callJson<{ ok: true; state: string }>(`/admin/bookings/${id}/return`, 'POST', body),

  vehicles: () => call<{ items: VehicleRow[] }>('/admin/vehicles'),
  vehicleDetail: (id: string) => call<VehicleDetail>(`/admin/vehicles/${id}`),
  setVehicleStatus: (id: string, status: VehicleRow['status']) =>
    callJson<{ ok: true }>(`/admin/vehicles/${id}/status`, 'PATCH', { status }),
  createVehicle: (body: {
    modelId: string;
    plate: string;
    year: number;
    color?: string;
    km?: number;
    status?: VehicleRow['status'];
    pricing: {
      eurPerDay: number;
      cvePerDay: number;
      eurCaucao?: number;
      cveCaucao?: number;
      weeklyDiscountPct?: number;
    };
  }) => callJson<{ id: string }>('/admin/vehicles', 'POST', body),
  patchVehicle: (id: string, patch: Partial<VehicleDetail & { pricing: VehicleDetail['pricing'] }>) =>
    callJson<{ ok: true }>(`/admin/vehicles/${id}`, 'PATCH', patch),
  deleteVehicle: (id: string) => callJson<{ ok: true }>(`/admin/vehicles/${id}`, 'DELETE'),
  uploadPhoto: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/v1/admin/vehicles/${id}/photos`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      // Surface the api's structured error so the UI can show why it failed (e.g. sharp
      // crashed on AVIF encoding, bucket missing, file too large).
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        detail?: string;
        code?: string;
      } | null;
      const msg = body?.detail || body?.error || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return (await res.json()) as { photo: PhotoDescriptor; totalCount: number };
  },
  uploadPromoImage: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/v1/admin/settings/home_promo/image`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
      throw new Error(body?.detail || body?.error || `HTTP ${res.status}`);
    }
    return (await res.json()) as { image: PromoImage };
  },
  patchPhoto: (id: string, key: string, body: { alt?: Tr; position?: number }) =>
    callJson<{ ok: true }>(`/admin/vehicles/${id}/photos/${encodeURIComponent(key)}`, 'PATCH', body),
  deletePhoto: (id: string, key: string) =>
    callJson<{ ok: true }>(`/admin/vehicles/${id}/photos/${encodeURIComponent(key)}`, 'DELETE'),

  vehicleAvailability: (id: string, from: string, to: string) =>
    call<AvailabilityResponse>(`/admin/vehicles/${id}/availability?from=${from}&to=${to}`),
  createBlock: (
    id: string,
    body: { fromAt: string; toAt: string; reason: 'maintenance' | 'manual_block'; notes?: Partial<Tr> },
  ) => callJson<{ id: string }>(`/admin/vehicles/${id}/blocks`, 'POST', body),
  deleteBlock: (id: string) => callJson<{ ok: true }>(`/admin/availability-blocks/${id}`, 'DELETE'),

  models: () => call<{ items: ModelRow[] }>('/admin/models'),
  createModel: (body: {
    slug: Tr;
    name: Tr;
    description: Tr;
    category: string;
    bodyType: 'suv' | 'sedan' | 'pickup';
    seats: number;
    transmission: 'AT' | 'MT';
    fuel: 'gasoline' | 'diesel' | 'hybrid';
    defaultColor?: string | null;
  }) => callJson<{ id: string }>('/admin/models', 'POST', body),
  patchModel: (
    id: string,
    body: {
      doors?: number | null;
      engineCc?: number | null;
      engineHp?: number | null;
      consumptionL100km?: number | null;
      luggageLarge?: number | null;
      luggageSmall?: number | null;
      acType?: string | null;
      multimedia?: string | null;
      tripStyles?: string[] | null;
      drivetrain?: string | null;
      unlimitedKm?: boolean | null;
      bookingEnabled?: boolean;
    },
  ) =>
    callJson<{
      ok: true;
      model?: {
        doors: number | null;
        engineCc: number | null;
        engineHp: number | null;
        consumptionL100km: string | null;
        luggageLarge: number | null;
        luggageSmall: number | null;
        acType: string | null;
        multimedia: string | null;
        tripStyles: string[];
        drivetrain: string | null;
        unlimitedKm: boolean | null;
        bookingEnabled: boolean;
      };
    }>(`/admin/models/${id}`, 'PATCH', body),

  locations: () => call<{ items: LocationRow[] }>('/admin/locations'),
  locationDetail: (id: string) => call<LocationDetail>(`/admin/locations/${id}`),
  createLocation: (body: LocationFormBody) => callJson<{ id: string }>('/admin/locations', 'POST', body),
  patchLocation: (id: string, body: Partial<LocationFormBody>) =>
    callJson<{ ok: true }>(`/admin/locations/${id}`, 'PATCH', body),
  deleteLocation: (id: string) => callJson<{ ok: true }>(`/admin/locations/${id}`, 'DELETE'),

  settings: () => call<{ items: { key: string; value: unknown; updatedAt: string }[] }>('/admin/settings'),
  settingByKey: (key: string) =>
    call<{ key: string; value: unknown; updatedAt?: string }>(`/admin/settings/${encodeURIComponent(key)}`),
  putSetting: async (key: string, value: unknown) => {
    const res = await fetch(`${API_BASE}/v1/admin/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) throw new Error(`api ${res.status}`);
    return (await res.json()) as { ok: true };
  },

  addons: () => call<{ items: AddonRow[] }>('/admin/addons'),
  createAddon: (body: AddonFormBody) => callJson<{ id: string }>('/admin/addons', 'POST', body),
  patchAddon: (id: string, body: Partial<AddonFormBody>) =>
    callJson<{ ok: true }>(`/admin/addons/${id}`, 'PATCH', body),
  deleteAddon: (id: string) => callJson<{ ok: true }>(`/admin/addons/${id}`, 'DELETE'),

  customers: (params: { q?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]);
    const path = qs.toString() ? `/admin/customers?${qs}` : '/admin/customers';
    return call<{ items: CustomerRow[]; paging: { limit: number; offset: number; count: number } }>(path);
  },

  reviews: (params: { state?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]);
    const path = qs.toString() ? `/admin/reviews?${qs}` : '/admin/reviews';
    return call<{ items: ReviewRow[]; paging: { limit: number; offset: number; count: number } }>(path);
  },
  moderateReview: (id: string, state: 'approved' | 'rejected') =>
    callJson<{ ok: true }>(`/admin/reviews/${id}`, 'PATCH', { state }),
  respondReview: (id: string, response: Tr) =>
    callJson<{ ok: true }>(`/admin/reviews/${id}/respond`, 'POST', { response }),

  customerDetail: (id: string) => call<CustomerDetail>(`/admin/customers/${id}`),
  blockCustomer: (id: string, reason: string) =>
    callJson<{ ok: true }>(`/admin/customers/${id}/block`, 'POST', { reason }),
  unblockCustomer: (id: string) => callJson<{ ok: true }>(`/admin/customers/${id}/unblock`, 'POST'),

  auditLog: (
    params: {
      actorUserId?: string;
      action?: string;
      targetTable?: string;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]);
    return call<{ items: AuditRow[]; paging: { limit: number; offset: number; count: number } }>(
      qs.toString() ? `/admin/audit-log?${qs}` : '/admin/audit-log',
    );
  },

  payments: (params: { state?: string; kind?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]);
    const path = qs.toString() ? `/admin/payments?${qs}` : '/admin/payments';
    return call<{ items: PaymentRow[]; paging: { limit: number; offset: number; count: number } }>(path);
  },
};
